<template>
    <!--    <label :for="field.key" class="block" :class="componentClass">-->
    <div :for="field.key" class="flex flex-col w-full mb-3">
        <label v-if="field.translated?.label" class="block text-xs font-bold tracking-wider mb-2 text-gray-500">
            {{ field.translated?.label }}
            <span v-if="isRequired" class="!text-red-500">*</span>
        </label>

        <div v-if="field.key === 'license_plate' || field.key === 'license_plate_confirmation'"
             class="flex items-center gap-2 w-full ring-1 rounded-[12px] px-2 py-1"
             :class="hasError ? 'ring-red-500' : isBelgiumPlate ? 'ring-[rgb(162,37,43)]' : 'ring-gray-200'"
             :style="plateContainerStyle"
             @mousedown.stop
             @click.stop="focusPlateInput($event)">
            <div v-if="plateImg.stars === 'yes'"
                 class="!bg-hu-green w-[32px] p-[2px] rounded-[4px] flex flex-col items-center justify-center flex-shrink-0 border border-gray-300 focus:ring-2 focus:ring-hu-green focus:border-hu-green h-[40px]">
                <span class="inline-flex h-[15px] w-[15px] shrink-0 items-center justify-center overflow-hidden">
                    <img
                        v-if="resolvedImageSrc"
                        :src="resolvedImageSrc"
                        :alt="imageAlt"
                        width="15"
                        height="15"
                        decoding="async"
                        class="h-[15px] w-[15px] max-h-[15px] max-w-[15px] object-contain"
                    />
                </span>
                <p class="!text-white !text-[10px] text-center whitespace-nowrap">{{ plateImg.code }}</p>
            </div>
            <div v-else-if="plateImg.stars === 'no'"
                 class="!bg-hu-green w-[32px] p-[2px] rounded-[4px] flex flex-col items-center justify-center flex-shrink-0 h-[40px]">
                <span class="inline-flex h-[15px] w-[15px] shrink-0 items-center justify-center overflow-hidden">
                    <img
                        v-if="getFlagByCountryCode"
                        :src="getFlagByCountryCode"
                        :alt="plateFlagAlt"
                        width="15"
                        height="15"
                        decoding="async"
                        class="h-[15px] w-[15px] max-h-[15px] max-w-[15px] object-contain"
                    />
                </span>
                <p class="!text-white !text-[10px] text-center whitespace-nowrap">{{ plateImg.code }}</p>
            </div>
            <div v-else
                 class="!bg-hu-green w-[32px] p-[2px] rounded-[4px] flex flex-col items-center justify-center flex-shrink-0 border border-gray-300 focus:ring-2 focus:ring-hu-green focus:border-hu-green h-[40px]">
                <span class="inline-flex h-[15px] w-[15px] shrink-0 items-center justify-center overflow-hidden">
                    <img
                        v-if="resolvedImageSrc"
                        :src="resolvedImageSrc"
                        :alt="imageAlt"
                        width="15"
                        height="15"
                        decoding="async"
                        class="h-[15px] w-[15px] max-h-[15px] max-w-[15px] object-contain"
                    />
                </span>
                <p class="!text-white !text-[10px] text-center whitespace-nowrap">{{ plateImg.code }}</p>
            </div>

            <input v-show="!isGermanyOrAustriaPlate"
                   ref="plateInput"
                   :type="getInputType(field.type)"
                   class="focus:ring-0 focus:ring-hu-green focus:border-hu-green border-0 outline-none text-gray-900 text-lg  tracking-wider uppercase placeholder:normal-case placeholder:font-normal placeholder:text-gray-400 transition-all w-100"
                   maxlength="12"
                   required
                   :name="field.key"
                   :value="stepData[field.key]"
                   @input="onPlateInput($event)"
                   :placeholder="plateImg && plateImg.placeholder ? plateImg.placeholder : field.translated?.placeholder"
            />

            <div v-show="isGermanyOrAustriaPlate"
                 class="flex items-center gap-2 w-full">
                <input
                    ref="firstSegmentInput"
                    v-model="plateSegments.seg1"
                    maxlength="3"
                    required
                    :placeholder="segmentPlaceholders[0]"
                    class="w-1/4 min-w-0 border border-gray-300 text-gray-900 text-lg  tracking-wider uppercase placeholder:normal-case placeholder:font-normal placeholder:text-gray-400 focus:ring-2 focus:ring-hu-green focus:border-hu-green transition-all h-[40px]"
                    :class="hasError ? 'ring-red-500' : 'ring-slate-200'"
                    @input="onSegmentInput('seg1')"
                />
                <div class="flex flex-col items-center justify-center gap-[4px] flex-shrink-0">
                    <span class="w-[8px] h-[8px] rounded-full bg-[rgb(221,221,221)]"></span>
                    <span class="w-[10px] h-[8px] rounded-full bg-[rgb(221,221,221)]"></span>
                </div>
                <input
                    v-model="plateSegments.seg2"
                    maxlength="3"
                    :placeholder="segmentPlaceholders[1]"
                    class="w-1/4 min-w-0 border border-gray-300 text-gray-900 text-lg  tracking-wider uppercase placeholder:normal-case placeholder:font-normal placeholder:text-gray-400 focus:ring-2 focus:ring-hu-green focus:border-hu-green transition-all h-[40px]"
                    :class="hasError ? 'ring-red-500' : 'ring-slate-200'"
                    @input="onSegmentInput('seg2')"
                />
                <input
                    v-model="plateSegments.seg3"
                    :maxlength="isGermanyPlate ? 4 : 6"
                    :placeholder="segmentPlaceholders[2]"
                    class="w-1/3 min-w-0 border border-gray-300 text-gray-900 text-lg  tracking-wider uppercase placeholder:normal-case placeholder:font-normal placeholder:text-gray-400 focus:ring-2 focus:ring-hu-green focus:border-hu-green transition-all h-[40px]"
                    :class="hasError ? 'ring-red-500' : 'ring-slate-200'"
                    @input="onSegmentInput('seg3')"
                />
            </div>
        </div>
        <div
            v-if="!(field.key === 'license_plate' || field.key === 'license_plate_confirmation')"
            class="relative w-full rounded-[12px]"
            :class="hasError ? 'ring-1 ring-red-500' : ''"
        >
            <!-- Иконка -->
            <img
                v-if="alternateEmail?.src"
                :src="alternateEmail.src"
                :alt="alternateEmail?.alt ?? ''"
                width="20"
                height="20"
                decoding="async"
                class="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 max-h-5 max-w-5 pointer-events-none object-contain"
            />

            <!-- Input -->
            <input
                :type="getInputType(field.type)"
                v-model="stepData[field.key]"
                :name="field.key"
                :placeholder="field.translated?.placeholder"
                :class="[
                    'block w-full py-4 border rounded-[12px] focus:ring-2 focus:ring-hu-green focus:border-hu-green text-gray-900 text-lg tracking-wider placeholder:normal-case placeholder:font-normal placeholder:text-gray-400 transition-all',
                    alternateEmail?.src ? 'pl-12 pr-4' : 'px-4',
                    hasError ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
                ]"
            />
        </div>
        <!--        <p v-if="hasError" class="!text-xs !text-red-500 mt-1">{{ field.translated.label }} is required</p>-->
    </div>

</template>

<script>
import { LICENSE_PLATES } from '../config/licensePlates';
import {
    filterLicensePlateInput,
    LICENSE_PLATE_LETTER_CLASS,
    sanitizeLicensePlateStored,
} from '../../../shared/licensePlateInput.js';
import { flagSrc } from '../../../utils/flagSrc.js';
export default {
    name: "TextInput",
    props: {
        field: Object,
        stepData: Object,
        imageSrc: {
            type: [Object, String],
            required: false,
            default: null
        },
        countries: { type: Array, default: () => [] },
        hasError: { type: Boolean, default: false },
        alternateEmail: { type: Object, default: null }
    },
    emits: ['clear-error'],
    data() {
        return {
            _syncingPlateSegments: false,
            plateSegments: {
                seg1: '',
                seg2: '',
                seg3: ''
            }
        };
    },
    methods: {
        onSegmentInput(segmentKey) {
            this.plateSegments[segmentKey] = filterLicensePlateInput(this.plateSegments[segmentKey]);
        },
        normalizePlateOutput(val) {
            if (this.isGermanyPlate) return val;
            if (this.isAustriaPlate) return val.toUpperCase();
            return val;
        },
        persistPlateSegments() {
            if (!this.isGermanyOrAustriaPlate) return;
            this.stepData[this.segmentStorageKey] = {
                seg1: this.plateSegments.seg1,
                seg2: this.plateSegments.seg2,
                seg3: this.plateSegments.seg3,
            };
        },
        syncPlateSegmentsFromStoredValue() {
            if (!this.isGermanyOrAustriaPlate) {
                this.plateSegments.seg1 = '';
                this.plateSegments.seg2 = '';
                this.plateSegments.seg3 = '';
                return;
            }

            const storedSegments = this.stepData[this.segmentStorageKey];
            if (storedSegments && typeof storedSegments === 'object') {
                this._syncingPlateSegments = true;
                this.plateSegments.seg1 = filterLicensePlateInput(String(storedSegments.seg1 ?? ''));
                this.plateSegments.seg2 = filterLicensePlateInput(String(storedSegments.seg2 ?? ''));
                this.plateSegments.seg3 = filterLicensePlateInput(String(storedSegments.seg3 ?? ''));
                this.$nextTick(() => {
                    this._syncingPlateSegments = false;
                });
                return;
            }

            const raw = String(this.stepData[this.field.key] ?? '').trim();
            if (!raw) {
                this.plateSegments.seg1 = '';
                this.plateSegments.seg2 = '';
                this.plateSegments.seg3 = '';
                return;
            }

            this._syncingPlateSegments = true;

            if (this.isGermanyPlate) {
                const normalized = sanitizeLicensePlateStored(raw, { preserveHyphen: true });
                if (normalized.includes('-')) {
                    const [region, ...restParts] = normalized.split('-');
                    const rest = restParts.join('-');
                    const letterClass = LICENSE_PLATE_LETTER_CLASS;
                    const m = (rest || '').match(new RegExp(`^([${letterClass}]*)(\\d*)$`, 'i'));
                    this.plateSegments.seg1 = filterLicensePlateInput((region || '').trim());
                    this.plateSegments.seg2 = m ? filterLicensePlateInput(m[1]) : '';
                    this.plateSegments.seg3 = m ? filterLicensePlateInput(m[2]) : '';
                } else {
                    const value = filterLicensePlateInput(normalized);
                    const letterClass = LICENSE_PLATE_LETTER_CLASS;
                    const m = value.match(new RegExp(`^([${letterClass}]+)([${letterClass}]+)(\\d+)$`));
                    if (m) {
                        this.plateSegments.seg1 = m[1];
                        this.plateSegments.seg2 = m[2];
                        this.plateSegments.seg3 = m[3];
                    }
                }
            } else if (this.isAustriaPlate) {
                const value = filterLicensePlateInput(raw);
                this.plateSegments.seg1 = value.slice(0, 3);
                this.plateSegments.seg2 = value.slice(3, 6);
                this.plateSegments.seg3 = value.slice(6, 12);
            }

            this.persistPlateSegments();
            this.$nextTick(() => {
                this._syncingPlateSegments = false;
            });
        },
        segmentsMatchStored(storedSegments) {
            if (!storedSegments || typeof storedSegments !== 'object') return false;
            return this.plateSegments.seg1 === String(storedSegments.seg1 ?? '')
                && this.plateSegments.seg2 === String(storedSegments.seg2 ?? '')
                && this.plateSegments.seg3 === String(storedSegments.seg3 ?? '');
        },
        tryRestorePlateSegments() {
            if (!this.isGermanyOrAustriaPlate || this._syncingPlateSegments) return;

            const storedSegments = this.stepData[this.segmentStorageKey];
            if (storedSegments && typeof storedSegments === 'object') {
                if (this.segmentsMatchStored(storedSegments)) return;
                this.syncPlateSegmentsFromStoredValue();
                return;
            }

            const stored = this.normalizePlateOutput(String(this.stepData[this.field.key] ?? '').trim());
            const current = this.normalizePlateOutput(String(this.unifiedPlateValue ?? '').trim());
            if (stored && stored !== current) {
                this.syncPlateSegmentsFromStoredValue();
            }
        },
        onPlateInput(ev) {
            const val = filterLicensePlateInput(ev.target?.value ?? '');
            if (ev.target && ev.target.value !== val) {
                ev.target.value = val;
            }
            this.stepData[this.field.key] = val;
            if (this.hasError && val) this.$emit('clear-error', this.field.key);
        },
        focusPlateInput(event) {
            const target = event?.target;

            // If user clicked directly on an input, do nothing
            if (target && target.tagName === 'INPUT') {
                return;
            }

            if (this.isGermanyOrAustriaPlate && this.$refs.firstSegmentInput) {
                setTimeout(() => this.$refs.firstSegmentInput.focus(), 0);
            } else if (this.$refs.plateInput) {
                const input = Array.isArray(this.$refs.plateInput)
                    ? this.$refs.plateInput[0]
                    : this.$refs.plateInput;

                if (input && typeof input.focus === 'function') {
                    setTimeout(() => input.focus(), 0);
                }
            }
        },
        getInputType(type) {
            const typeMap = {
                email_input: "email",
                text_input: "text",
                date_input: "date"
            };
            return typeMap[type] || "text";
        }
    },
    computed: {
        segmentStorageKey() {
            return `${this.field.key}_segments`;
        },

        isRequired() {
            const r = this.field.validation_rules;
            if (!r) return false;

            const rules = Array.isArray(r) ? r : [r];

            if (rules.includes('required')) return true;

            for (const rule of rules) {
                if (typeof rule === 'string' && rule.startsWith('required_if:form.need_an_invoice,true')) {
                    return true;
                }
            }

            return false;
        },

        isGermanyPlate() {
            return this.stepData?.plate_country === 'DE';
        },

        isAustriaPlate() {
            return this.stepData?.plate_country === 'AT';
        },

        isGermanyOrAustriaPlate() {
            return this.isGermanyPlate || this.isAustriaPlate;
        },

        isBelgiumPlate() {
            return this.stepData?.plate_country === 'BE';
        },

        isNetherlandsPlate() {
            return this.stepData?.plate_country === 'NL';
        },

        segmentPlaceholders() {
            if (this.isGermanyPlate) return ['B', 'AB', '1234'];
            if (this.isAustriaPlate) return ['122', 'ABC', '55645'];
            return ['', '', ''];
        },

        unifiedPlateValue() {
            const { seg1, seg2, seg3 } = this.plateSegments;
            if (this.isGermanyPlate) {
                return (seg1 ? `${seg1}-` : '') + seg2 + seg3;
            }
            if (this.isAustriaPlate) {
                return (seg1 + seg2 + seg3).toUpperCase();
            }
            return '';
        },

        plateContainerStyle() {
            if (this.isNetherlandsPlate && this.plateImg?.color) {
                return { backgroundColor: this.plateImg.color };
            }
            return {};
        },

        plateImg() {
            const code = this.stepData?.plate_country;
            return LICENSE_PLATES[code] ?? { stars: 'no', code: '', placeholder: '', color: 'rgba(244,244,244,1)' };
        },

        resolvedImageSrc() {
            if (!this.imageSrc) return null;
            if (typeof this.imageSrc === 'string') return this.imageSrc;
            return this.imageSrc.src ?? this.imageSrc.url ?? null;
        },

        imageAlt() {
            if (!this.imageSrc || typeof this.imageSrc !== 'object') return 'Vignette Image';
            return this.imageSrc.translated?.alt ?? 'Vignette Image';
        },

        getFlagByCountryCode() {
            if (this.stepData?.plate_country && this.countries) {
                const country = this.countries.find(item => item.code === this.stepData.plate_country);
                return country?.flag ? flagSrc(country.flag) : null;
            }
            return null;
        },

        plateFlagAlt() {
            if (!this.stepData?.plate_country || !this.countries) return '';
            const country = this.countries.find(item => item.code === this.stepData.plate_country);
            return country?.translated?.name || this.stepData.plate_country;
        }
    },
    watch: {
        unifiedPlateValue(val) {
            if (!this.isGermanyOrAustriaPlate || this._syncingPlateSegments) return;
            const out = this.normalizePlateOutput(val);
            this.stepData[this.field.key] = out;
            this.persistPlateSegments();
            if (this.hasError && out) {
                this.$emit('clear-error', this.field.key);
            }
        },
        'stepData.plate_country'(newVal, oldVal) {
            if (newVal === oldVal) return;
            if (newVal === 'DE' || newVal === 'AT') {
                this.$nextTick(() => this.tryRestorePlateSegments());
                return;
            }
            if (oldVal === 'DE' || oldVal === 'AT') {
                this.plateSegments.seg1 = '';
                this.plateSegments.seg2 = '';
                this.plateSegments.seg3 = '';
            }
        }
    },

    mounted() {
        this.$nextTick(() => {
            this.tryRestorePlateSegments();
        });
    },

    created() {
        this._plateSegmentsRestoreStop = this.$watch(
            () => [
                this.stepData?.plate_country,
                this.stepData?.[this.field.key],
                this.stepData?.[this.segmentStorageKey],
            ],
            () => {
                this.$nextTick(() => {
                    this.tryRestorePlateSegments();
                });
            }
        );
        this.$watch(
            () => this.stepData[this.field.key],
            (val) => {
                if (this.hasError && val?.toString().trim() !== '') {
                    this.$emit('clear-error', this.field.key);
                }
            }
        );
    },

    beforeUnmount() {
        if (this._plateSegmentsRestoreStop) {
            this._plateSegmentsRestoreStop();
        }
    }

};
</script>
